import {
   Accordion,
   AccordionButton,
   AccordionIcon,
   AccordionItem,
   AccordionPanel,
   Box,
   Button,
   useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useInstitutionsContext } from 'contexts/institutions'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
   DetranManagementReportResponse,
   getDetranManagementReportFile,
   getDetranManagementReports
} from 'services/report'
import { generateBlobFile } from 'utils/blob'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import {
   columnLabels,
   defaultLoaders,
   initialStateFilterDetranManagement
} from './constants'
import { FormDetranManagementReportValue } from './form'
import { schema } from './schema'
import { FilterDetranManagementReport } from './types'

export const DetranManagementReport: React.FC = () => {
    const toast = useToast()
    const { currentAgent } = useInstitutionsContext()

    const form = useMemo(() => FormDetranManagementReportValue(), [])

    const {
        register,
        formState: { errors },
        watch,
        handleSubmit,
        reset
    } = useForm<FilterDetranManagementReport>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: initialStateFilterDetranManagement
    })

    const [loading, setLoading] = useState(false)
    const [loadingFile, setLoadingFile] = useState(false)

    const [filter, setFilter] = useState<FilterDetranManagementReport>(
        initialStateFilterDetranManagement
    )

    const [pageReports, setPageReports] = useState(1)
    const [limitReports, setLimitReports] = useState(10)
    const [totalPages, setTotalPages] = useState(1)

    const [table, setTable] = useState<TableBuildProps<string>>({
        header: []
    })

    const validarAgente = () => {
        if (!currentAgent?.cnpj) {
            toast({
                title: 'Agente Financeiro obrigatório',
                description:
                    'Selecione um Agente Financeiro no cabeçalho para consultar o relatório.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })

            return false
        }

        return true
    }

    const montarQuery = (
        filtros: FilterDetranManagementReport,
        page = pageReports,
        limit = limitReports
    ) => {
        return removeEmptyValues({
            ufDetran: filtros.ufDetran,
            tipoRelatorio: filtros.tipoRelatorio,
            referencia: filtros.referencia,
            cnpjAgenteFinanceiro: currentAgent?.cnpj?.replace(/\D/g, '') ?? '',
            pagina: page - 1,
            limite: limit
        })
    }

    const fetchListReports = async (
        filtros: FilterDetranManagementReport,
        page = pageReports,
        limit = limitReports
    ) => {
        if (!validarAgente()) return

        setLoading(true)

        try {
            const response = await getDetranManagementReports(
                montarQuery(filtros, page, limit) as any
            )

            const registros = response.data.registros || []

            setTotalPages(response.data.totalPaginas || 1)

            setTable({
                header: buildHeaders(registros),
                body: buildTable(response.data)
            })
        } catch (error: any) {
            toast({
                title: 'Sentimos muito!',
                description: await getErrorMessage(error),
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
        }
    }

    const onSubmit = (value: FilterDetranManagementReport) => {
        setFilter(value)
        setPageReports(1)
        fetchListReports(value, 1, limitReports)
    }

    const handleClear = () => {
        reset(initialStateFilterDetranManagement)
        setFilter(initialStateFilterDetranManagement)
        setPageReports(1)
        setLimitReports(10)
        setTotalPages(1)
        setTable({ header: [] })
    }

    const handlePage = (page: number) => {
        if (page === pageReports) return

        setPageReports(page)
        fetchListReports(filter, page, limitReports)
    }

    const handleLimit = (limit: number) => {
        setPageReports(1)
        setLimitReports(limit)
        fetchListReports(filter, 1, limit)
    }

    const fetchGenerateFile = (type: string) => {
        handleSubmit(value => downloadFiles(type, value))()
    }

    const downloadFiles = async (
        type: string,
        filtros: FilterDetranManagementReport
    ) => {
        if (!validarAgente()) return

        setLoadingFile(true)

        try {
            const response = await getDetranManagementReportFile({
                ...(montarQuery(filtros, pageReports, limitReports) as any),
                tipo: type
            })

            generateBlobFile(response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error: any) {
            toast({
                title: 'Sentimos muito!',
                description: await getErrorMessage(error),
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    return (
        <>
            <Box display="flex" justifyContent="flex-end" marginBottom="3">
                <FileGenerator
                    callAPI={fetchGenerateFile as any}
                    loading={loadingFile}
                />
            </Box>

            <Accordion
                allowMultiple
                backgroundColor="white"
                marginBottom={4}
                defaultIndex={[0]}
            >
                <AccordionItem>
                    <h2>
                        <AccordionButton
                            backgroundColor="white"
                            className="white-hover"
                        >
                            <Box
                                as="h2"
                                flex="1"
                                textAlign="left"
                                fontSize={20}
                                fontWeight={700}
                            >
                                Filtrar Relatório(s):
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>

                    <AccordionPanel pb={4} backgroundColor="white">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box backgroundColor="white">
                                <Layout type="row" gutter={{ y: 1.5 }}>
                                    {form.map(
                                        ({ layout, input, select }, key) => (
                                            <Layout {...layout} key={key}>
                                                {select ? (
                                                    <SelectBuild
                                                        {...select}
                                                        err={
                                                            errors[
                                                                select.id as keyof FilterDetranManagementReport
                                                            ]
                                                        }
                                                        {...register(
                                                            select.id as keyof FilterDetranManagementReport
                                                        )}
                                                        value={watch(
                                                            select.id as keyof FilterDetranManagementReport
                                                        )}
                                                    />
                                                ) : (
                                                    input && (
                                                        <InputBuild
                                                            {...input}
                                                            err={
                                                                errors[
                                                                    input.id as keyof FilterDetranManagementReport
                                                                ]
                                                            }
                                                            {...register(
                                                                input.id as keyof FilterDetranManagementReport
                                                            )}
                                                            value={watch(
                                                                input.id as keyof FilterDetranManagementReport
                                                            )}
                                                        />
                                                    )
                                                )}
                                            </Layout>
                                        )
                                    )}
                                </Layout>

                                <Box
                                    paddingY={8}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={6}
                                >
                                    <Button
                                        colorScheme="primary"
                                        variant="outline"
                                        onClick={handleClear}
                                    >
                                        Limpar Campos
                                    </Button>

                                    <Button
                                        colorScheme="primary"
                                        type="submit"
                                        isLoading={loading}
                                    >
                                        Filtrar
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>

            <TableBuild
                header={table.header}
                body={table.body}
                loading={{
                    status: loading,
                    loader: defaultLoaders
                }}
                pagination={{
                    page: pageReports,
                    limit: limitReports,
                    totalPages
                }}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            />
        </>
    )
}

const buildHeaders = (registros: Record<string, any>[]) => {
    const keys = new Set<string>()

    registros.forEach(registro => {
        Object.keys(registro ?? {}).forEach(key => {
            const value = registro[key]

            if (value !== null && value !== undefined && value !== '') {
                keys.add(key)
            }
        })
    })

    return Array.from(keys).map((key, index) => ({
        name: key,
        text: columnLabels[key] ?? humanizarLabel(key),
        position: index
    }))
}

const buildTable = (response: DetranManagementReportResponse) => {
    return (response.registros || []).map(registro => {
        const row: Record<string, string | number> = {}

        Object.keys(registro ?? {}).forEach(key => {
            row[key] = formatarValor(key, registro[key])
        })

        return row
    })
}

const humanizarLabel = (value: string) => {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, str => str.toUpperCase())
}

const formatarValor = (key: string, value: any): string | number => {
    if (value === null || value === undefined) return ''

    if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não'
    }

    if (key.toLowerCase().includes('valor') && typeof value === 'number') {
        return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        })
    }

    if (
        key.toLowerCase().includes('data') &&
        typeof value === 'string' &&
        value.includes('T')
    ) {
        return new Date(value).toLocaleDateString('pt-BR')
    }

    return String(value)
}

const getErrorMessage = async (error: any): Promise<string> => {
    const defaultMessage = 'Ocorreu um erro, tente novamente mais tarde.'

    if (!error?.response) {
        return defaultMessage
    }

    const data = error.response.data

    if (data instanceof Blob) {
        try {
            const text = await data.text()
            const parsed = JSON.parse(text)

            return (
                parsed?.erros?.[0]?.mensagem ??
                parsed?.[0]?.mensagem ??
                parsed?.message ??
                parsed?.title ??
                defaultMessage
            )
        } catch {
            return defaultMessage
        }
    }

    return (
        data?.erros?.[0]?.mensagem ??
        data?.[0]?.mensagem ??
        data?.message ??
        data?.title ??
        defaultMessage
    )
}